/**
 * ContextSwitcher — presentational only. It renders exactly the contexts the
 * headless hook resolved from server-side role rows; it never derives or
 * widens access itself, and it renders nothing when the account holds a single
 * context.
 */
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIdentityContexts, type IdentityContext } from "@/hooks/use-identity-contexts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Check, ChevronDown, ChevronsUpDown, LogOut, Sparkles, User } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useUserAvatar } from "@/hooks/use-user-avatar";
import ProfileAvatar from "@/components/consumer/ProfileAvatar";

interface Props {
  /**
   * admin = outline button, consumer = grape pill, pill = full-width sidebar
   * pill, avatar = single identity control (avatar/person trigger, contexts +
   * log out in one menu).
   */
  variant?: "admin" | "consumer" | "pill" | "avatar";
  /** consumer variant hides below sm by default (landing nav); set false to always show. */
  hideOnMobile?: boolean;
  /**
   * Extra actions for the `avatar` variant, rendered BETWEEN the contexts and
   * log out (e.g. "Add another card"). Surface-specific shortcuts live here so
   * the single identity control can absorb a screen's bespoke menu without a
   * second button appearing next to it.
   */
  extraItems?: ReactNode;
  /** Label shown on the trigger of the `pill` variant. */
  currentLabel?: string;
  className?: string;
}

export default function ContextSwitcher({
  variant = "consumer",
  hideOnMobile = true,
  extraItems,
  currentLabel = "Cardmoji Platform",
  className = "",
}: Props) {
  const { contexts, activeId, select, loading } = useIdentityContexts();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { signOut } = useAuth();
  const { url: avatarUrl, backgroundColour: avatarBg } = useUserAvatar();

  /**
   * The `avatar` variant is the account's ONLY identity control on its surface
   * — it owns log out — so it must render even for a single-context account.
   * The other variants are pure switchers and stay hidden when there is
   * nothing to switch to.
   */
  if (loading || (contexts.length <= 1 && variant !== "avatar")) return null;

  const go = (ctx: IdentityContext) => {
    select(ctx);
    navigate(ctx.path);
  };

  const groups: { title: string; items: IdentityContext[] }[] = [
    { title: "You", items: contexts.filter((c) => c.kind === "consumer" || c.kind === "creator") },
    { title: "Platform", items: contexts.filter((c) => c.kind === "platform") },
    { title: "Tenants", items: contexts.filter((c) => c.kind === "tenant") },
  ].filter((g) => g.items.length > 0);

  /**
   * Which context is the account actually IN right now. The platform surface is
   * not a tenant, so it cannot be derived from adminTenantId — it is the URL
   * that says whether you are on /platform/* (platform sidebar) or /admin/*
   * (tenant-admin sidebar). Without this, switching to the platform console
   * gave no confirmation and looked like nothing happened.
   */
  const isCurrent = (c: IdentityContext) => {
    if (c.kind === "platform") return pathname.startsWith("/platform");
    if (c.kind === "tenant") return c.id === activeId && pathname.startsWith("/admin");
    return pathname.startsWith(c.path);
  };

  const body = (
    <div className="max-h-[60vh] w-[248px] overflow-y-auto p-1.5" data-testid="context-switcher-menu">
      {groups.map((g) => (
        <div key={g.title} className="mb-1">
          <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {g.title}
          </p>
          {g.items.map((c) => (
            <button
              key={`${c.kind}:${c.id}`}
              type="button"
              onClick={() => go(c)}
              className="flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-left text-[14px] font-medium text-foreground hover:bg-muted"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{c.label}</span>
                {c.caption ? (
                  <span className="block truncate text-[11px] font-normal text-muted-foreground">{c.caption}</span>
                ) : null}
              </span>
              {isCurrent(c) ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  if (variant === "admin") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={className} data-testid="context-switcher">
            Switch context
            <ChevronDown className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          {body}
        </PopoverContent>
      </Popover>
    );
  }

  if (variant === "pill") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="context-switcher"
            className={`flex w-full items-center gap-2 rounded-full border bg-card px-3 py-2 text-left text-sm shadow-sm hover:bg-muted/60 ${className}`}
          >
            <ArrowLeftRight className="h-4 w-4 shrink-0 text-primary" />
            <span className="flex-1 truncate">{currentLabel}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          {body}
        </PopoverContent>
      </Popover>
    );
  }

  if (variant === "avatar") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Your account and contexts"
            data-testid="identity-menu-trigger"
            /* shrink-0 + aspect-square are part of the control, not the host's job:
               inside a tight flex header the 44px box was being compressed to ~14px
               wide while keeping its height, which is what made the round icon read
               as an oblong. Baked in here so every host gets the round shape. */
            className={`grid h-[44px] w-[44px] aspect-square shrink-0 place-items-center overflow-hidden rounded-full border border-ink/20 bg-white text-ink transition-colors hover:border-ink ${className}`}
          >
            {avatarUrl ? (
              /* ONE renderer: the same cutout-on-colour path the profile uses. */
              <ProfileAvatar
                avatarUrl={avatarUrl}
                backgroundColour={avatarBg}
                className="h-full w-full"
                data-testid="identity-menu-avatar"
              />
            ) : (
              /* Head-and-shoulders fallback when the account has no avatar yet. */
              <User className="h-5 w-5" strokeWidth={2} data-testid="identity-menu-avatar-fallback" />
            )}

          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto rounded-2xl p-0">
          {/* Contexts first — "Your cards" IS the consumer context, so there is
              no separate account button to duplicate it. */}
          {contexts.length > 0 ? body : null}
          {extraItems ? (
            <div className={contexts.length > 0 ? "border-t border-ink/10 p-1.5" : "p-1.5"}>{extraItems}</div>
          ) : null}
          {/* Log out is a different KIND of action: separated by a rule and
              placed last so it can't be mis-tapped while switching. */}
          <div className="border-t border-ink/10 p-1.5">
            <button
              type="button"
              data-testid="identity-menu-logout"
              onClick={async () => {
                await signOut();
                navigate("/");
              }}
              className="flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-left text-[14px] font-medium text-foreground hover:bg-muted"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="flex-1">Log out</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="context-switcher"
          className={`min-h-[44px] items-center gap-1.5 rounded-full border border-ink/20 bg-white px-[18px] text-[15px] font-semibold text-ink transition-colors hover:border-ink ${
            hideOnMobile ? "hidden sm:flex" : "flex"
          } ${className}`}
        >
          Switch
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto rounded-2xl p-0">
        {body}
      </PopoverContent>
    </Popover>
  );
}
